import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { attendanceApi } from "../attendanceApi";

export const attendanceKeys = {
  list: (filters) => ["attendance", filters],
  day: (id) => ["attendanceDay", id],
  locations: (id) => ["attendanceLocations", id],
  forReview: (filters) => ["sessionsForReview", filters],
  employeeSettings: (filters) => ["attendanceSettings", "employees", filters],
  roleSettings: () => ["attendanceSettings", "roles"],
  imports: (params) => ["biometricImports", params],
  import: (id) => ["biometricImport", id],
};

// Anything that changes a day or a session: lists, the day, its locations,
// the validation queue (+ badge) and DTR details that show the days.
const DAY_KEYS = [
  ["attendance"],
  ["attendanceDay"],
  ["attendanceLocations"],
  ["sessionsForReview"],
  ["dtr"],
];

const useAttendanceMutation = (fn, keys = DAY_KEYS) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () =>
      keys.forEach((queryKey) => qc.invalidateQueries({ queryKey })),
  });
};

/* ---------- Days & sessions ---------- */
export const useAttendanceList = (filters) =>
  useQuery({
    queryKey: attendanceKeys.list(filters),
    queryFn: () => attendanceApi.getList(filters),
    placeholderData: keepPreviousData,
  });

export const useAttendanceLocations = (id) =>
  useQuery({
    queryKey: attendanceKeys.locations(id),
    queryFn: () => attendanceApi.getLocations(id),
    enabled: !!id,
    retry: false,
  });

export const useSessionsForReview = (filters, { enabled = true } = {}) =>
  useQuery({
    queryKey: attendanceKeys.forReview(filters),
    queryFn: () => attendanceApi.getForReview(filters),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useReviewSession = () =>
  useAttendanceMutation(({ id, action, remarks }) =>
    attendanceApi.reviewSession(id, { action, remarks }),
  );

/** Confirm / reject many periods or whole days at once (DTR entries list). */
export const useReviewSessionsBulk = () =>
  useAttendanceMutation((body) => attendanceApi.reviewSessionsBulk(body));

export const useCreateAttendanceDay = () =>
  useAttendanceMutation((body) => attendanceApi.createDay(body));
export const useAddSession = () =>
  useAttendanceMutation(({ attendanceId, ...body }) =>
    attendanceApi.addSession(attendanceId, body),
  );
export const useEditSession = () =>
  useAttendanceMutation(({ sessionId, ...body }) =>
    attendanceApi.editSession(sessionId, body),
  );
export const useUpdateAttendanceDay = () =>
  useAttendanceMutation(({ id, ...body }) => attendanceApi.updateDay(id, body));

/* ---------- Check-in settings ---------- */
export const useEmployeeCheckInSettings = (filters) =>
  useQuery({
    queryKey: attendanceKeys.employeeSettings(filters),
    queryFn: () => attendanceApi.getEmployeeSettings(filters),
    placeholderData: keepPreviousData,
  });

export const useUpdateEmployeeCheckInSettings = () =>
  useAttendanceMutation(
    (body) => attendanceApi.updateEmployeeSettings(body),
    [["attendanceSettings"]],
  );

export const useRoleCheckInSettings = () =>
  useQuery({
    queryKey: attendanceKeys.roleSettings(),
    queryFn: attendanceApi.getRoleSettings,
  });

export const useUpdateRoleCheckInSetting = () =>
  useAttendanceMutation(
    ({ roleId, ...changes }) =>
      attendanceApi.updateRoleSetting(roleId, changes),
    [["attendanceSettings"]],
  );

/* ---------- Biometrics ---------- */
export const useBiometricImports = (params) =>
  useQuery({
    queryKey: attendanceKeys.imports(params),
    queryFn: () => attendanceApi.getBiometricImports(params),
    placeholderData: keepPreviousData,
  });

export const useBiometricImport = (id) =>
  useQuery({
    queryKey: attendanceKeys.import(id),
    queryFn: () => attendanceApi.getBiometricImport(id),
    enabled: !!id,
  });

/** Dry run changes nothing; a real import refreshes imports and every attendance view. */
export const useImportBiometrics = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args) => attendanceApi.importBiometrics(args),
    onSuccess: (_res, { dryRun, linkSuggested }) => {
      if (linkSuggested) qc.invalidateQueries({ queryKey: ["biometricLinks"] });
      if (dryRun) return;
      [["biometricImports"], ...DAY_KEYS].forEach((queryKey) =>
        qc.invalidateQueries({ queryKey }),
      );
    },
  });
};

/* ---------- Biometric layouts + device IDs ---------- */
export const useBiometricLayouts = () =>
  useQuery({
    queryKey: ["biometricLayouts"],
    queryFn: attendanceApi.getBiometricLayouts,
  });
export const useBiometricPresets = () =>
  useQuery({
    queryKey: ["biometricPresets"],
    queryFn: attendanceApi.getBiometricPresets,
    staleTime: 30 * 60 * 1000,
  });
export const useSaveBiometricLayout = () =>
  useAttendanceMutation(
    ({ id, ...body }) =>
      id
        ? attendanceApi.updateBiometricLayout(id, body)
        : attendanceApi.createBiometricLayout(body),
    [["biometricLayouts"], ["biometricLinks", "systems"]],
  );
export const useDeleteBiometricLayout = () =>
  useAttendanceMutation(
    (id) => attendanceApi.deleteBiometricLayout(id),
    [["biometricLayouts"]],
  );
export const useInspectBiometrics = () =>
  useMutation({
    mutationFn: ({ file, sheet }) =>
      attendanceApi.inspectBiometrics(file, sheet),
  });
export const useBiometricSystems = () =>
  useQuery({
    queryKey: ["biometricLinks", "systems"],
    queryFn: attendanceApi.getBiometricSystems,
  });
export const useBiometricLinks = (params, { enabled = true } = {}) =>
  useQuery({
    queryKey: ["biometricLinks", params],
    queryFn: () => attendanceApi.getBiometricLinks(params),
    placeholderData: keepPreviousData,
    enabled,
  });
export const useSaveBiometricLinks = () =>
  useAttendanceMutation(
    (body) => attendanceApi.saveBiometricLinks(body),
    [["biometricLinks"]],
  );
